export const sanitizeUrl = (url?: string | null) => {
  if (!url) return ""

  let cleanUrl = url.replace(/[`'"]/g, "").trim()
  if (!cleanUrl) return ""

  if (cleanUrl.startsWith("ipfs://")) {
    cleanUrl = cleanUrl.replace("ipfs://", "https://ipfs.io/ipfs/")
  }

  if (cleanUrl.startsWith("//")) {
    cleanUrl = `https:${cleanUrl}`
  }

  // Support user/API values like "web-service.nftbaz.com/path/to/image.jpg"
  if (!cleanUrl.startsWith("http") && /^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(cleanUrl)) {
    cleanUrl = `https://${cleanUrl}`
  }

  if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
    try {
      const parsed = new URL(cleanUrl)
      // Prevent passing origin-only URLs (e.g. https://web-service.nftbaz.com/) to next/image.
      if (parsed.pathname === "/") return ""
      return parsed.toString()
    } catch {
      return ""
    }
  }

  if (cleanUrl.startsWith("/")) {
    return cleanUrl
  }

  // Anything else has no scheme and is not root-relative either (e.g. a bare
  // storage key like "member-nft/101544/-3254668-101544.jpg" — observed live
  // on a real NFT record, missing the host every sibling record on the same
  // account carries). next/image's loader requires "/..." or a full absolute
  // URL and throws `TypeError: Invalid URL` on anything else, which crashed
  // the whole page (caught by the nearest error boundary) rather than the one
  // broken image. Reject it instead: the caller's existing "no media"
  // placeholder is the honest outcome for a value this function cannot resolve
  // — inventing a host to guess the caller meant would be exactly the kind of
  // fabricated data this codebase avoids.
  return ""
}

export const shortenAddress = (address?: string | null, head = 3, tail = 3) => {
  const value = (address ?? "").trim()
  if (!value) return ""
  if (value.length <= head + tail + 3) return value
  return `${value.slice(0, head)}...${value.slice(-tail)}`
}

/**
 * Formats a token/listing price for display. Accepts a number OR a string,
 * since some listing endpoints send very small values in scientific
 * notation (e.g. "1.0E-14") that `Number()` parses fine but that must never
 * be shown to users as-is.
 *
 * `Intl.NumberFormat`'s decimal style (what `toLocaleString` uses) never
 * emits scientific notation regardless of magnitude — it always expands to
 * a plain decimal string — so a high `maximumFractionDigits` is all that's
 * needed to stop tiny prices from being rounded down to "0" (the default of
 * 3 fraction digits would otherwise turn 0.00000000000001 into "0.000").
 * Trailing zeros aren't padded since no `minimumFractionDigits` is set, so
 * ordinary prices like 2.5 still render as "2.5", not "2.50000000000000000000".
 */
export const formatTokenPrice = (value: number | string, locale: "fa-IR" | "en-US" = "en-US"): string => {
  const num = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(num)) return "0"
  return num.toLocaleString(locale, { maximumFractionDigits: 20 })
}
